import { type Activity } from '@/models/Activity';

import { ref, watchEffect } from 'vue';
import { defineStore } from 'pinia';
import { useRoute } from 'vue-router';

import { useLocalStorage } from '@/composables/useStorage';

export const useActivityPlaylistStore = defineStore('activityPlaylist', () => {
  const route = useRoute();

  const { get: isActivityAutoplayDisabled, set: setActivityAutoplayDisabled } = useLocalStorage(
    'isActivityAutoplayDisabled',
  );
  const { set: saveLastPlayedActivityId } = useLocalStorage('lastPlayedActivityId');

  const playlist = ref<Activity[]>([]);
  const currentActivity = ref<Activity>();
  const nextActivity = ref<Activity>();
  const showNextActivitySelect = ref<boolean>(false);
  const isAutoplayDisabled = ref<boolean>(isActivityAutoplayDisabled());

  const setPlaylist = (newPlaylist: Activity[]) => {
    playlist.value = newPlaylist;
  };

  const setShowNextActivitySelect = (newValue: boolean) => {
    showNextActivitySelect.value = newValue;
  };

  const setIsAutoplayDisabled = (newValue: boolean) => {
    isAutoplayDisabled.value = newValue;
    setActivityAutoplayDisabled(newValue);
  };

  watchEffect(() => {
    if (!route.params.activityId && nextActivity.value) {
      setShowNextActivitySelect(true);
      return;
    }

    if (!route.params.activityId || playlist.value.length === 0) return;

    const currentActivityIndex = playlist.value.findIndex(
      (activity) => activity.courseCode === String(route.params.activityId),
    );

    if (currentActivityIndex === -1) {
      currentActivity.value = undefined;
      return;
    }

    currentActivity.value = playlist.value[currentActivityIndex];

    saveLastPlayedActivityId(currentActivity.value.courseCode);

    if (currentActivityIndex < playlist.value.length - 1) {
      nextActivity.value = playlist.value[currentActivityIndex + 1];
    } else {
      nextActivity.value = undefined;
    }

    setShowNextActivitySelect(false);
  });

  return {
    setPlaylist,
    setShowNextActivitySelect,
    setIsAutoplayDisabled,
    currentActivity,
    nextActivity,
    isAutoplayDisabled,
    showNextActivitySelect,
  };
});
